import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import {
  isRecipeType,
  pluralizeIfNeeded,
  reCalculateMacros,
} from "../../../../../utils/nutrition-util";
import { truncateText } from "../../../../../utils/string-helpers";

/**
 * Animation component
 * @param {*} param0
 * @returns
 */
export const InsertedContainerWithAnimations = ({
  onEnd,
  hideAnimation,
  selectedItem,
  quantity,
  unitOfMeasure,
}) => {
  const { t } = useTranslation("athlete-portal");
  const {
    name,
    quantityDescription,
    proteins: itemProteins,
    carbs: itemCarbs,
    fats: itemFats,
    calories: itemCalories,
    unitOfMeasure: itemUnitOfMeasure,
    quantityNumber: itemQuantityNumber,
    type,
    measurementsValue,
    brand,
  } = selectedItem || {
    name: "",
    quantityDescription: "",
    proteins: 0,
    carbs: 0,
    fats: 0,
    calories: 0,
    type: "",
    unitOfMeasure: 0,
    quantityNumber: 0,
    measurementsValue: "",
  };
  const displayQuantity = quantity === 0 ? "" : String(quantity);

  const baseDescription =
    quantityDescription?.toLowerCase() === "serving" ||
    quantityDescription?.toLowerCase() === "servings"
      ? quantity > 1
        ? "servings"
        : "serving"
      : quantityDescription?.toLowerCase() === "batch"
        ? quantity > 1
          ? "batches"
          : "batch"
        : unitOfMeasure === "custom"
          ? pluralizeIfNeeded(
              quantityDescription?.toLowerCase() ?? "servings",
              quantity
            )
          : (quantityDescription ?? "servings");

  const quantityText =
    unitOfMeasure === "custom"
      ? baseDescription
      : unitOfMeasure === "Select"
        ? ""
        : unitOfMeasure;

  const itemDetails = {
    itemProteins,
    itemCarbs,
    itemFats,
    itemCalories,
    foodKey: "foodId",
    customQuantityNumber: quantity,
    quantityNumber: itemQuantityNumber,
    customUnitOfMeasure: unitOfMeasure,
    unitOfMeasure: itemUnitOfMeasure,
    measurementsValue,
    type,
    skipRecalculation: false,
    ...(isRecipeType(type) && {
      recipeMacrosBase: quantityDescription,
    }),
  };

  let proteins, carbs, fats, calories;

  if (type === "meal" && Array.isArray(selectedItem?.foods)) {
    const total = selectedItem.foods.reduce(
      (acc, food) => {
        if (!food?.foodId) return acc;
        const mealQuantityNumber = quantity * food.quantityNumber;
        const macros = reCalculateMacros({
          itemDetails: {
            itemProteins: food?.foodId?.proteins || 0,
            itemCarbs: food?.foodId?.carbs || 0,
            itemFats: food?.foodId?.fats || 0,
            itemCalories: food?.foodId?.calories || 0,
            foodKey: "foodId",
            customQuantityNumber: mealQuantityNumber,
            quantityNumber: food?.foodId.quantityNumber || 1,
            customUnitOfMeasure: food.unitOfMeasure,
            unitOfMeasure: food?.foodId.unitOfMeasure || "custom",
            measurementsValue: food?.foodId.measurementsValue || 0,
            type: food.type,
            skipRecalculation: false,
            ...((food?.foodId?.type === "recipe" ||
              food.type === "external_recipe") && {
              recipeMacrosBase: food?.foodId?.quantityDescription,
            }),
          },
        });

        acc.proteins += Number(macros.proteins) || 0;
        acc.carbs += Number(macros.carbs) || 0;
        acc.fats += Number(macros.fats) || 0;
        acc.calories += Number(macros.calories) || 0;
        return acc;
      },
      { proteins: 0, carbs: 0, fats: 0, calories: 0 }
    );

    proteins = isNaN(total.proteins) ? "" : total.proteins;
    carbs = isNaN(total.carbs) ? "" : total.carbs;
    fats = isNaN(total.fats) ? "" : total.fats;
    calories = isNaN(total.calories) ? "" : total.calories;
  } else {
    const result = reCalculateMacros({ itemDetails });

    proteins = isNaN(result.proteins) ? "" : result.proteins;
    carbs = isNaN(result.carbs) ? "" : result.carbs;
    fats = isNaN(result.fats) ? "" : result.fats;
    calories = isNaN(result.calories) ? "" : result.calories;
  }

  return (
    <div
      className={`content animated ${hideAnimation ? "hide" : ""}`}
      onAnimationEnd={() => {
        onEnd();
      }}
    >
      <div className="info-food info-food-button">
        <div>
          <span>
            {brand && !name.toLowerCase().includes(brand.toLowerCase())
              ? `${name} (${brand})`
              : name}
          </span>
        </div>
        <div className="nutrition-info">
          <span>
            {t("foodItems.protein")} {proteins}g{" "}
          </span>
          <span>&#8226;</span>
          <span>
            {t("foodItems.carbs")} {carbs}g
          </span>
          <span>&#8226;</span>
          <span>
            {t("foodItems.fat")} {fats}g
          </span>
          <span>&#8226;</span>
          <span>
            {t("foodItems.calories")} {calories}
          </span>
        </div>
      </div>
      <div className="actions">
        <div className="number-container">
          <button>
            <SvgIcon src="/img/svg/angle-down.svg" />
          </button>
          <div className="flex items-center">
            <span>
              {truncateText(`${displayQuantity} ${quantityText}`, 12)}
            </span>
          </div>
          <button>
            <SvgIcon src="/img/svg/angle-up.svg" />
          </button>
        </div>
        <button className="button-cancel">
          <img src="/img/new-icons/red-cross.svg" />
        </button>
      </div>
    </div>
  );
};

InsertedContainerWithAnimations.propTypes = {
  onEnd: PropTypes.func,
  hideAnimation: PropTypes.bool,
  selectedItem: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.oneOf([null]),
  ]),
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unitOfMeasure: PropTypes.string,
};
